/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Dominik Boeck <d.boeck@techdivision.com>
 */

/**
 * Retrieves the value of a specified query parameter from the current URL.
 *
 * @param {string} param - The name of the query parameter to retrieve.
 * @returns {string|null} - The value of the specified query parameter, or null if the parameter is not present.
 *
 * @example
 * // Assuming the URL is: https://example.com/?exampleParam=123&anotherParam=456
 * const paramName = "exampleParam";
 * const paramValue = getQueryParam(paramName);
 * console.log(The value of the parameter "${paramName}" is: ${paramValue});
 * // Output: The value of the parameter "exampleParam" is: 123
 */
export function getQueryParam(param) {
  // Get the entire URL
  const url = window.location.href;
  // Create a URL object to parse the URL components
  const urlObj = new URL(url);
  // Get the search parameters from the URL
  const params = new URLSearchParams(urlObj.search);
  // Return the value of the requested parameter
  return params.get(param);
}

/**
 * Retrieves all query parameters from the current URL as an object.
 *
 * @returns {Object} - An object containing all query parameters and their values.
 *
 * @example
 * // Assuming the URL is: https://example.com/?param1=value1&param2=value2
 * const queryParams = getAllQueryParams();
 * console.log(queryParams);
 * // Output: { param1: "value1", param2: "value2" }
 */
export function getAllQueryParams() {
  // Get the entire URL
  const url = window.location.href;
  // Create a URL object to parse the URL components
  const urlObj = new URL(url);
  // Get the search parameters from the URL
  const params = new URLSearchParams(urlObj.search);
  // Create an empty object to hold the query parameters
  const queryParams = {};
  // Iterate over all query parameters and add them to the object
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of params.entries()) {
    queryParams[key] = value;
  }
  // Return the object containing all query parameters
  return queryParams;
}
